import { getLatLngForXY } from "@/lib/leaflet-util";
import { getImageDimensions } from "@/lib/util";
import { useDeskStore } from "@/pinia/desk-store";
import { floorService } from "@/services/floor";
import { officeService } from "@/services/office";
import { reservationService } from "@/services/reservation";
import { createAxiosInstance } from "@/services/util";
import { Desk, Floor, Office, Reservation } from "@/swagger/models";
import { DeskAvailabilityStatus } from "@/types/misc";
import { defineStore } from "pinia";
import Vue from "vue";
import { DateTime, Interval } from "luxon";
import { getWorkdayInterval } from "@ciao/shared/src/lib/date";
import { LatLngBounds, latLng, latLngBounds, LatLngTuple } from "leaflet";

// const axios = createAxiosInstance();

export interface FloorplanMarker {
    desk: Desk;
    reservations?: Reservation[];
    status?: DeskAvailabilityStatus;
    latLng: LatLngTuple;
}

const deskStore = useDeskStore();

export const useFloorplanStore = defineStore({
    id: "floorplan",
    state: () => ({
        /**
         * Selected office
         */
        office: <Office | null>null,
        /**
         * Selected floor
         */
        floor: <Floor | null>null,
        /**
         * Available floors
         */
        floors: <Floor[] | null>null,
        /**
         * Selected date
         */
        date: DateTime.local().startOf("day"),
        /**
         * Selected interval (start / end time)
         */
        interval: Interval.fromDateTimes(
            DateTime.local().startOf("day").plus({ hours: 9 }),
            DateTime.local().endOf("day").plus({ hours: 17 }),
        ),
        /**
         * Are we in edit mode?
         */
        editMode: false,
        /**
         * Bounds for the map, based on the floorplanUrl
         */
        bounds: <LatLngBounds | null>null,

        markers: <FloorplanMarker[]>[],

        selectedMarker: <FloorplanMarker | null>null,

        futureReservations: [] as Reservation[],
    }),
    getters: {
        placedMarkers() {
            return this.markers.filter((m) => m.latLng);
        },
        unplacedMarkers() {
            return this.markers.filter((m) => !m.latLng);
        },
    },
    actions: {
        async toggleMode() {
            this.editMode = !this.editMode;
        },

        /**
         * Loads an office by ID
         * @param id
         */
        async loadOffice(id: number) {
            const office = await officeService.get(id);
            Vue.set(this, "office", office);
            await this.loadFloors();
        },
        /**
         * Load available floors for selected office
         */
        async loadFloors() {
            if (!this.office) return;

            const floors = await floorService.findNew({ params: { office: this.office.id } });
            Vue.set(this, "floors", floors.data);
            Vue.set(this, "floor", null);
            Vue.set(this, "bounds", null);
            if (floors.data.length) await this.setFloor(floors.data[0]);
        },
        /**
         * Set selected floor and calculate bounds
         * @param floor
         */
        async setFloor(floor: Floor) {
            const floorplanSize = await getImageDimensions(floor.floorplanUrl);
            const mapWidth = floorplanSize.width / 1000;
            const mapHeight = floorplanSize.height / 1000;
            const southWest = latLng(0, 0);
            const northEast = latLng(mapHeight, mapWidth);
            const bounds = latLngBounds(southWest, northEast);

            Vue.set(this, "bounds", bounds);
            Vue.set(this, "floor", floor);

            await this.loadSlotMarkers();
        },
        async loadSlotMarkers() {
            if (!this.floor) return;
            if (!this.office) return;

            // Todo: handle timezones -> if start is at 0:00, its utc 22:00 the previous day.
            // Maybe discuss the best approach.
            // const utcStart = this.interval.start.toUTC();

            const deskRes = await createAxiosInstance().get(`/slots/floors/${this.floor.id}/desks`, {
                params: {
                    date: this.date.toSQLDate(),
                    start: this.interval.start.toUTC().toLocaleString(DateTime.TIME_24_SIMPLE),
                    end: this.interval.end.toUTC().toLocaleString(DateTime.TIME_24_SIMPLE),
                },
            });

            Vue.set(
                this,
                "markers",
                deskRes.data.map((d) => ({
                    desk: d,
                    reservations: d.reservations,
                    status: d.status,
                    latLng: this.bounds ? getLatLngForXY(this.bounds, d.x, d.y) : null,
                })),
            );

            // If the selected marker is still available, re-select it. Otherwise, set to null
            // We do this as this is now a new object, with new reservations, even though its for the same marker
            if (this.selectedMarker) {
                const matchingMarker = this.markers.find((m) => m.desk.id === this.selectedMarker.desk.id);
                if (matchingMarker) await this.selectMarker(matchingMarker);
                else await this.selectMarker(null);
            }
        },
        async placeMarker(marker: FloorplanMarker) {
            await deskStore.patch(marker.desk.id, { x: 0.5, y: 0.5 });
            await this.loadSlotMarkers();
        },
        async unplaceMarker(marker: FloorplanMarker) {
            marker.latLng = null;
            await deskStore.patch(marker.desk.id, { x: null, y: null });
            if (this.selectedMarker === marker) this.selectedMarker = null;
        },
        async selectMarker(marker: FloorplanMarker | null) {
            this.selectedMarker = marker;
            this.futureReservations = [];

            if (!marker) return;

            // Load future reservations
            const res = await reservationService.findNew({
                params: {
                    desk: marker.desk.id,
                    after: this.date.startOf("day").plus({ days: 1 }).toSQLDate(),
                    "sort[date]": "ASC",
                },
            });

            this.futureReservations = res.data;
        },
    },
});
